/**
 * ElementTile — a compact square tile showing one signal's score + name.
 * Used in the Explore tab's signal grid (Adir's THEMES section); click
 * to open the drawer.
 */

import React from 'react';
import { LockOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { formatBipolarScore } from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import type { ScoreNode } from '../types';

export interface ElementTileProps {
  node: ScoreNode;
  onClick?: () => void;
}

/**
 * ElementTile — compact tile for the Explore tab's signal grid.
 *
 * A top accent in the element's ring colour (same colour as the score number)
 * gives the grid more life and a quick at-a-glance read of each element's
 * standing; the rest stays neutral white chrome.
 */
export const ElementTile: React.FunctionComponent<ElementTileProps> = ({
  node,
  onClick,
}) => {
  const navigate = useScopedNavigate();
  const isLocked = !!node.locked;
  const scoreColour = colourForScore(node.canonical, node.value);
  // Locked tiles upsell instead of opening the (locked) drawer.
  const handleClick = isLocked
    ? () => navigate(MARKETER_UPGRADE_ROUTE)
    : onClick;
  return (
    <button
      type="button"
      onClick={handleClick}
      className="lift"
      style={{
        textAlign: 'left',
        cursor: handleClick ? 'pointer' : 'default',
        background: '#fff',
        border: '1px solid #E5E7EB',
        borderTop: `3px solid ${isLocked ? '#CBD5E1' : scoreColour}`,
        borderRadius: 13,
        padding: '13px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
      }}
    >
      {isLocked ? (
        <span
          style={{
            fontSize: 20,
            color: '#9ca3af',
            lineHeight: 1,
          }}
        >
          <LockOutlined />
        </span>
      ) : (
        <span
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: scoreColour,
            letterSpacing: '-0.03em',
            lineHeight: 1,
          }}
        >
          {formatBipolarScore(node.value)}
        </span>
      )}
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          color: '#64748B',
          lineHeight: 1.3,
        }}
      >
        {node.display}
      </span>
      {isLocked && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 600,
            color: '#F59E0B',
            marginTop: 2,
          }}
        >
          <LockOutlined style={{ fontSize: 10 }} />
          <FormattedMessage
            id="marketerView.lock.unlock"
            defaultMessage="Upgrade to unlock"
          />
        </span>
      )}
    </button>
  );
};
